import React, { useState, useEffect } from 'react';
import { FaExchangeAlt, FaPlane, FaCar, FaShip, FaPhoneAlt } from 'react-icons/fa';
import airports from '../data/airports';
import CruiseHighlights from './Highlights';
import { useNavigate } from 'react-router-dom';
import Renderads from './advertisement';
import { v4 as uuidv4 } from 'uuid';
import MexicoDealsCarousel from './TravelCarousel';
import TravelRoutes from './TravelRoutes';
import BottomPromoCard from './BottomPromoCard';
import DealsSection from './DealsSection';

const getSuggestions = (input) => {
  if (!input) return [];
  return airports
    .filter(
      (airport) =>
        airport.code.toLowerCase().startsWith(input.toLowerCase()) ||
        airport.name.toLowerCase().includes(input.toLowerCase())
    )
    .slice(0, 50);
};

const renderSuggestionsDropdown = (input, setInput, setShowSuggestions) => {
  const suggestions = getSuggestions(input);
  return (
    suggestions.length > 0 && (
      <ul className="absolute left-0 z-50 min-w-[200%] bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto mt-1 text-sm">
        {suggestions.map((airport) => (
          <li
            key={airport.code}
            onMouseDown={() => {
              setInput(`${airport.name} (${airport.code})`);
              setShowSuggestions(false);
            }}
            className="px-4 py-3 cursor-pointer hover:bg-gray-100 text-[15px]"
          >
            <span className="font-semibold text-gray-900">{airport.name}</span>{' '}
            <span className="text-teal-500">({airport.code})</span>
          </li>
        ))}
      </ul>
    )
  );
};

const TabbedBookingForm = () => {
  const [activeTab, setActiveTab] = useState('Flights');
  const [tripType, setTripType] = useState('Round Trip');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [departureDate, setDepartureDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [travelers] = useState(1);

  const [pickupFrom, setPickupFrom] = useState('');
  const [dropLocation, setDropLocation] = useState('');
  const [pickupDate, setPickupDate] = useState('');

  const [cruisePort, setCruisePort] = useState('');
  const [cruiseLine, setCruiseLine] = useState('');
  const [adultCount, setAdultCount] = useState(2);
  const [childCount, setChildCount] = useState(0);
  const [infantCount, setInfantCount] = useState(0);

  // Traveler dropdown
  const [showTravelerDropdown, setShowTravelerDropdown] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState(1);

  const cabinClassMap = {
    1: 'Economy',
    2: 'Premium Economy',
    3: 'Business',
    4: 'First',
  };

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }, []);

  const swapLocations = () => {
    const temp = from;
    setFrom(to);
    setTo(temp);
  };

  const navigate = useNavigate();

  const handleSubmit = () => {
    const formData = {
      from,
      to,
      departureDate,
      arrivalDate,
      travelers,
      tripType,
      adults,
      children,
      infants,
      cabinClass,
    };

    if (activeTab === 'Flights') {
      navigate('/flightresults', { state: formData });
    } else {
      navigate('/cruises', { state: formData });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const searchId = uuidv4();
    localStorage.setItem('search_id', searchId);
    handleSubmit();
  };

  return (
    <div className="w-full bg-gradient-to-br from-[#E8F9F6] via-[#F5FCFB] to-white pb-20">


      <Renderads imgNumber={1} />

      {/* MAIN CARD */}
      <div className="bg-white text-gray-900 p-8 rounded-3xl shadow-xl max-w-7xl mx-auto mt-10 border border-gray-200">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
              BookTrip4Me
            </h1>   
          </div>
        </div>

        {/* TABS */}
        <div className="flex space-x-6 border-b pb-3 mb-6 border-gray-200">
          {[{ label: 'Flights', icon: <FaPlane /> }, { label: 'Car Rentals', icon: <FaCar /> }, { label: 'Cruises', icon: <FaShip /> }].map(
            ({ label, icon }) => (
              <button
                key={label}
                onClick={() => setActiveTab(label)}
                className={`flex items-center gap-2 text-sm font-semibold pb-2 transition ${
                  activeTab === label
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {icon} {label}
              </button>
            )
          )}
        </div>

        {/* FLIGHTS */}
        {activeTab === 'Flights' && (
          <>
            {/* TRIP TYPE */}
            <div className="flex flex-wrap gap-3 mb-4">
              {['Round Trip', 'One Way'].map((type) => (
                <button
                  key={type}
                  onClick={() => setTripType(type)}
                  className={`px-4 py-2 rounded-full text-sm border transition ${
                    tripType === type
                      ? 'bg-teal-500 text-white border-teal-500 shadow'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-100'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* FORM */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className="flex flex-wrap md:flex-nowrap gap-4 items-end">

                {/* FROM */}
                <div className="relative w-full md:w-[18%]">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    From
                  </label>
                  <input
                    type="text"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      setShowFromSuggestions(true);
                    }}
                    onBlur={() => setTimeout(() => setShowFromSuggestions(false), 150)}
                    onFocus={() => setShowFromSuggestions(true)}
                    className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm text-gray-900 focus:ring-2 focus:ring-teal-500"
                    placeholder="City or airport"
                    required
                  />
                  {showFromSuggestions &&
                    renderSuggestionsDropdown(from, setFrom, setShowFromSuggestions)}
                </div>

                {/* SWAP */}
                <button
                  onClick={swapLocations}
                  type="button"
                  className="p-3 bg-gray-100 border border-gray-300 rounded-full hover:bg-gray-200 text-teal-600"
                >
                  <FaExchangeAlt />
                </button>

                {/* TO */}
                <div className="relative w-full md:w-[18%]">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    To
                  </label>
                  <input
                    type="text"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                      setShowToSuggestions(true);
                    }}
                    onBlur={() => setTimeout(() => setShowToSuggestions(false), 150)}
                    onFocus={() => setShowToSuggestions(true)}
                    className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm text-gray-900 focus:ring-2 focus:ring-teal-500"
                    placeholder="City or airport"
                    required
                  />
                  {showToSuggestions &&
                    renderSuggestionsDropdown(to, setTo, setShowToSuggestions)}
                </div>

                {/* DEPARTURE */}
                <div className="w-full md:w-[14%]">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    Departure
                  </label>
                  <input
                    type="date"
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                    className="border border-gray-300 rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                {/* RETURN */}
                {tripType === 'Round Trip' && (
                  <div className="w-full md:w-[14%]">
                    <label className="text-xs text-gray-600 font-medium mb-1 block">
                      Return
                    </label>
                    <input
                      type="date"
                      value={arrivalDate}
                      onChange={(e) => setArrivalDate(e.target.value)}
                      className="border border-gray-300 rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}

                {/* TRAVELERS */}
                <div className="relative w-full md:w-[20%]">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    Travelers & Cabin
                  </label>

                  <div
                    onClick={() => setShowTravelerDropdown(!showTravelerDropdown)}
                    className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full cursor-pointer text-sm flex items-center justify-between"
                  >
                    <span>
                      {adults + children + infants}{" "}
                      {adults + children + infants > 1 ? "Travelers" : "Traveler"}
                    </span>
                    <span className="text-xs text-teal-600">
                      {cabinClassMap[cabinClass]}
                    </span>
                  </div>

                  {showTravelerDropdown && (
                    <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 w-72 z-50">

                      {[ 
                        { label: 'Adult (18+)', value: adults, setValue: setAdults, min: 1 },
                        { label: 'Child (2-17)', value: children, setValue: setChildren, min: 0 },
                        { label: 'Infant (0-2)', value: infants, setValue: setInfants, min: 0 },
                      ].map(({ label, value, setValue, min }) => (
                        <div key={label} className="flex justify-between items-center mb-3">
                          <span className="text-sm text-gray-800">{label}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              className="w-8 h-8 rounded-md bg-gray-100 border border-gray-300 hover:bg-gray-200 text-lg text-teal-600"
                              onClick={() => setValue(Math.max(value - 1, min))}
                            >
                              –
                            </button>
                            <span className="w-4 text-center text-gray-900">{value}</span>
                            <button
                              type="button"
                              className="w-8 h-8 rounded-md bg-gray-100 border border-gray-300 hover:bg-gray-200 text-lg text-teal-600"
                              onClick={() => setValue(value + 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}

                      <select
                        className="mt-4 w-full border border-gray-300 bg-white rounded-lg p-2 text-sm text-gray-800 focus:ring-2 focus:ring-teal-500"
                        value={cabinClass}
                        onChange={(e) => setCabinClass(Number(e.target.value))}
                      >
                        <option value={1}>Economy</option>
                        <option value={2}>Premium Economy</option>
                        <option value={3}>Business</option>
                        <option value={4}>First</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* SEARCH */}
                <div className="w-full md:w-[10%]">
                  <button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition"
                    onClick={handleSearch}
                  >
                    Search
                  </button>

                  {/* Mobile call button */}
                  <a
                    href="tel:+18887112909"
                    className="mt-3 w-full block md:hidden bg-teal-100 text-teal-700 border border-teal-300 py-2 px-2 rounded-lg font-semibold text-center text-xs shadow"
                  >
                    Call for Deals
                  </a>
                </div>
              </div>
            </form>
          </>
        )}

        {/* CAR RENTALS */}
        {activeTab === 'Car Rentals' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex flex-wrap md:flex-nowrap gap-4 items-end"
          >
            <div className="w-full md:w-1/2">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Pickup From</label>
              <input
                type="text"
                value={pickupFrom}
                onChange={(e) => setPickupFrom(e.target.value)}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            <div className="w-full md:w-1/2">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Drop Location</label>
              <input
                type="text"
                value={dropLocation}
                onChange={(e) => setDropLocation(e.target.value)}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            <div className="w-full md:w-1/2">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Pickup Date</label>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            <div className="w-full md:w-[10%]">
              <button
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-xl shadow"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
          </form>
        )}

        {/* CRUISES */}
        {activeTab === 'Cruises' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex flex-wrap md:flex-nowrap gap-4 items-end"
          >
            <div className="w-full md:w-[20%]">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Departure Port</label>
              <input
                type="text"
                value={cruisePort}
                onChange={(e) => setCruisePort(e.target.value)}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            <div className="w-full md:w-[20%]">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Cruise Line</label>
              <input
                type="text"
                value={cruiseLine}
                onChange={(e) => setCruiseLine(e.target.value)}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            <div className="w-full md:w-[10%]">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Adults</label>
              <input
                type="number"
                min={1}
                value={adultCount}
                onChange={(e) => setAdultCount(Number(e.target.value))}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="w-full md:w-[10%]">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Children</label>
              <input
                type="number"
                min={0}
                value={childCount}
                onChange={(e) => setChildCount(Number(e.target.value))}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="w-full md:w-[10%]">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Infants</label>
              <input
                type="number"
                min={0}
                value={infantCount}
                onChange={(e) => setInfantCount(Number(e.target.value))}
                className="border border-gray-300 bg-white rounded-xl px-4 py-3 w-full text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="w-full md:w-[10%]">
              <button
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-xl shadow"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
          </form>
        )}
      </div>

      {/* BELOW SECTIONS */}
      <div className="mt-20">
        <DealsSection />
        <CruiseHighlights />
        <TravelRoutes />
        <BottomPromoCard />
      </div>

    </div>
  );
};

export default TabbedBookingForm;
